import {injectable} from 'inversify'
import {makeObservable} from 'mobx'
import i18next from 'i18next'

import {TerminalType} from '@/constants'
import {getPortalCurrencies} from '@/constants/currencies'
import {posCardSchemes} from '@/services/card-scheme'
import {FilterFormFieldType} from '@/types'
import {POSFilterType} from '@/types/transactions'
import {getNotEmptyValues} from '@/utils'
import {getPOSCaptureMethods} from '@/services/pos-transaction'
import {BaseFilterContainerStore} from '@/stores/shared'
import {StoreMidTerminalFilterStore} from '@/stores/store-and-terminals'

@injectable()
export class POSFiltersStore extends BaseFilterContainerStore<POSFilterType> {
    private _smtfStore = new StoreMidTerminalFilterStore({
        terminalType: TerminalType.POS
    })

    constructor(
        private options: {
            hiddenFields?: string[]
        } = {}
    ) {
        super()

        makeObservable(this, {
            ...super.getAnnotationsMap()
        })
    }

    get loadingState() {
        return this._smtfStore.loadingState
    }

    get filter() {
        return {
            ...this._filter,
            store: this._smtfStore.selectedStore,
            mid: this._smtfStore.selectedMid,
            terminalId: this._smtfStore.selectedTerminal
        }
    }

    get fields(): FilterFormFieldType[] {
        const fields: FilterFormFieldType[] = [
            {
                name: 'transactionId',
                label: i18next.t('globals:fields.transactionId'),
                type: 'input'
            },
            {
                name: 'store',
                label: i18next.t('globals:fields.store'),
                type: 'selectWithTwoLabels',
                isHidden: this._smtfStore.storesAsSelectItemsWithAll.length === 0,
                data: {
                    optionsWithTwoLabels: this._smtfStore.storesAsSelectItemsWithAll
                }
            },
            {
                name: 'mid',
                label: i18next.t('globals:fields.mid'),
                type: 'select',
                isHidden: this._smtfStore.midsAsSelectItemWithAll.length === 0,
                data: {
                    options: this._smtfStore.midsAsSelectItemWithAll
                }
            },

            {
                name: 'isDcc',
                label: i18next.t('globals:fields.dcc'),
                type: 'select',
                data: {
                    options: [
                        {label: i18next.t('globals:filter.all'), value: ''},
                        {label: i18next.t('globals:values.yes'), value: true as unknown as string},
                        {label: i18next.t('globals:values.no'), value: false as unknown as string}
                    ]
                }
            },

            {
                name: 'terminalId',
                label: i18next.t('globals:fields.terminalId'),
                type: 'select',
                isHidden: this._smtfStore.terminalsAsSelectItemWithAll.length === 0,
                data: {
                    options: this._smtfStore.terminalsAsSelectItemWithAll
                }
            },
            {
                name: 'currency',
                label: i18next.t('globals:fields.currency'),
                type: 'select',
                data: {
                    options: [
                        {label: i18next.t('globals:filter.all'), value: ''},
                        ...getPortalCurrencies
                    ]
                }
            },
            {
                name: 'cardMask',
                label: i18next.t('globals:fields.cardMask'),
                type: 'input',
                rules: [
                    {min: 3, message: i18next.t('globals:validation.common.minLength', {min: 3})}
                ]
            },
            {
                name: 'cardScheme',
                label: i18next.t('globals:fields.cardScheme'),
                type: 'select',
                data: {
                    options: [
                        {label: i18next.t('globals:filter.all'), value: ''},
                        ...posCardSchemes()
                    ]
                }
            },
            {
                name: 'captureMethod',
                label: i18next.t('globals:fields.captureMethod'),
                type: 'select',
                data: {
                    options: [
                        {label: i18next.t('globals:filter.all'), value: ''},
                        ...getPOSCaptureMethods()
                    ]
                }
            }
        ]

        return fields.filter((f) => !this.options.hiddenFields?.includes(f.name))
    }

    isFilterSelected(field: string, value: any): boolean {
        if (field === 'store' && this._smtfStore.storesAsSelectItemsWithAll.length === 1) {
            return false
        }
        return super.isFilterSelected(field, value)
    }

    getInitialFilter() {
        return {
            transactionId: '',
            currency: '',
            cardMask: '',
            isDcc: '',
            cardScheme: '',
            captureMethod: ''
        }
    }

    setFilter(filter: Partial<POSFilterType>) {
        const {store, mid, terminalId, ...rest} = filter
        super.setFilter(rest)

        if (store !== undefined) {
            this._smtfStore.setStore(store)
        }
        if (mid !== undefined) {
            this._smtfStore.setMid(mid)
        }
        if (terminalId !== undefined) {
            this._smtfStore.setTerminal(terminalId as string)
        }
    }

    clear() {
        this._smtfStore.clear()
        super.clear()
    }

    getFilterForBackend = (): Omit<Partial<POSFilterType>, 'store' | 'mid'> & {mids?: string[]} => {
        return getNotEmptyValues({
            ...this._smtfStore.requestForServer,
            transactionId: this.submittedFilter.transactionId,
            currency: this.submittedFilter.currency,
            cardScheme: this.submittedFilter.cardScheme,
            captureMethod: this.submittedFilter.captureMethod,
            cardMask: this.submittedFilter.cardMask,
            isDcc: this.submittedFilter.isDcc
        })
    }
}
